import {
  type BizcycleProject,
  type BusinessStep,
  compareDatesNullsLast,
  compareText,
  DEFAULT_STEP,
  type JiraIssue,
  jiraPriorityRank,
} from '@pmai/contracts';

/** Client, project, assignee and Jira status filters (FR-BIZ-012); '' = all. */
export interface BizFilters {
  customerId: string;
  projectId: string;
  assignee: string;
  status: string;
}
export const NO_FILTERS: BizFilters = { customerId: '', projectId: '', assignee: '', status: '' };

export const isFiltered = (f: BizFilters) => Object.values(f).some((v) => v !== '');

/** Client and project only — the assignee and status choices come from these tasks. */
export const inScope = (i: JiraIssue, f: BizFilters) =>
  (!f.projectId || i.projectId === f.projectId) && (!f.customerId || i.customerId === f.customerId);

/** `assigneeOf` names an unassigned task the way the filter lists it. */
export const matches = (i: JiraIssue, f: BizFilters, assigneeOf: (i: JiraIssue) => string) =>
  inScope(i, f) && (!f.assignee || assigneeOf(i) === f.assignee) && (!f.status || i.statusName === f.status);

/** A task without a step counts as development (FR-BIZ-010). */
export const stepOf = (i: JiraIssue): BusinessStep => i.step ?? DEFAULT_STEP;

/** Approved tasks per step and how many of them burn (FR-BIZ-010). */
export function stepCounts(issues: readonly JiraIssue[]) {
  const counts = new Map<BusinessStep, { count: number; burning: number }>();
  for (const i of issues) {
    const c = counts.get(stepOf(i)) ?? { count: 0, burning: 0 };
    counts.set(stepOf(i), { count: c.count + 1, burning: c.burning + (i.burning ? 1 : 0) });
  }
  return counts;
}

export const scopedProjects = (projects: readonly BizcycleProject[], f: BizFilters) =>
  projects.filter(
    (p) => (!f.projectId || p.id === f.projectId) && (!f.customerId || p.customerId === f.customerId),
  );

/** Technologies of the filtered projects, most projects first (FR-BIZ-011, FR-BIZ-017). */
export function competences(projects: readonly BizcycleProject[], nameOf: (id: string) => string) {
  const byTech = new Map<string, BizcycleProject[]>();
  for (const p of projects) for (const t of p.technologyIds) byTech.set(t, [...(byTech.get(t) ?? []), p]);
  return [...byTech]
    .map(([technologyId, list]) => ({ technologyId, projects: list }))
    .sort(
      (a, b) =>
        b.projects.length - a.projects.length || compareText(nameOf(a.technologyId), nameOf(b.technologyId)),
    );
}

export const SORTS = ['due', 'priority', 'updated', 'name'] as const;
export type IssueSort = (typeof SORTS)[number];

/** FR-BIZ-016: due date (none last), priority (Highest first), last update (newest first), summary. */
export function sortIssues(list: readonly JiraIssue[], by: IssueSort): JiraIssue[] {
  const compare: Record<IssueSort, (a: JiraIssue, b: JiraIssue) => number> = {
    due: (a, b) => compareDatesNullsLast(a.dueDate, b.dueDate),
    priority: (a, b) => jiraPriorityRank(a.priority) - jiraPriorityRank(b.priority),
    updated: (a, b) => compareDatesNullsLast(b.jiraUpdated, a.jiraUpdated),
    name: (a, b) => compareText(a.summary, b.summary),
  };
  return [...list].sort(compare[by]);
}
